// Seeded presets + the canned-reply generator for the COMMAND v2 prototype.
// Replies are deterministic (seeded hash) so demos behave consistently.

import type { ProtoChainStep, ProtoPreset } from "./types";

// ---------------------------------------------------------------------------
// Deterministic pseudo-randomness

export function hash01(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
}

export function pick<T>(items: T[], seed: string): T {
  return items[Math.floor(hash01(seed) * items.length) % items.length];
}

let idCounter = 0;
export function nextId(prefix: string): string {
  idCounter += 1;
  return `${prefix}_${Date.now().toString(36)}${idCounter}`;
}

// ---------------------------------------------------------------------------
// Seeded presets

export const CHAIN_PRESET_ID = "preset_full_cycle";
export const ACTIVITY_REPORT_PRESET_ID = "preset_activity_report";

export const FULL_CYCLE_STEPS: ProtoChainStep[] = [
  {
    id: "step_brief",
    title: "Brief arrives",
    promptText:
      "A new client brief just arrived. Summarise the requirements, budget signals and timeline into a one-page brief summary.",
  },
  {
    id: "step_methodology",
    title: "Match methodology",
    promptText:
      "Match this brief against our delivery methodologies and recommend the best-fit approach with reasoning.",
  },
  {
    id: "step_prep",
    title: "Call prep sheet",
    promptText:
      "Build a discovery call prep sheet for this brief: key questions, stakeholders to confirm, and risks to probe.",
  },
  {
    id: "step_call_analysis",
    title: "Discovery call analysis",
    promptText:
      "Analyse the discovery call notes: extract the confirmed requirements, objections raised, and decision criteria.",
  },
  {
    id: "step_proposal",
    title: "Proposal draft",
    promptText:
      "Draft a proposal outline based on the brief and the discovery call: scope, approach, timeline and next steps.",
  },
];

export function buildSeedPresets(): ProtoPreset[] {
  return [
    {
      id: CHAIN_PRESET_ID,
      title: "Discovery → Proposal (full cycle)",
      description:
        "Walk a new brief through the full cycle — summary, methodology, call prep, call analysis, proposal.",
      promptText: FULL_CYCLE_STEPS[0].promptText,
      category: "custom",
      favourite: true,
      order: 1,
      chain: { steps: FULL_CYCLE_STEPS },
    },
    {
      id: ACTIVITY_REPORT_PRESET_ID,
      title: "Agent activity report",
      description:
        "Per-team activity for the last 7 days — calls, meetings, analyses and tasks, with one headline insight.",
      promptText:
        "Run the agent activity report for the last 7 days across all teams.",
      category: "management",
      favourite: true,
      order: 2,
    },
    {
      id: "preset_exec_briefing",
      title: "Weekly Executive Briefing",
      description:
        "Headline metrics, wins, risks and the decisions that need attention this week.",
      promptText:
        "Prepare the weekly executive briefing: headline metrics, wins, risks and what needs a decision this week.",
      category: "executive",
      favourite: true,
      order: 3,
    },
    {
      id: "preset_pipeline",
      title: "Pipeline health snapshot",
      description:
        "Stage conversion, stalled deals and forecast confidence at a glance.",
      promptText:
        "Give me a pipeline health snapshot: stage conversion, stalled deals and forecast confidence.",
      category: "executive",
      favourite: false,
      order: 4,
    },
    {
      id: "preset_stage_brief",
      title: "Summarise a new brief",
      description: "Turn an incoming client brief into a one-page summary.",
      promptText: FULL_CYCLE_STEPS[0].promptText,
      category: "management",
      favourite: false,
      order: 5,
    },
    {
      id: "preset_stage_methodology",
      title: "Match methodology to a brief",
      description: "Recommend the best-fit delivery approach with reasoning.",
      promptText: FULL_CYCLE_STEPS[1].promptText,
      category: "management",
      favourite: false,
      order: 6,
    },
    {
      id: "preset_stage_prep",
      title: "Build a call prep sheet",
      description: "Key questions, stakeholders and risks before a discovery call.",
      promptText: FULL_CYCLE_STEPS[2].promptText,
      category: "management",
      favourite: false,
      order: 7,
    },
    {
      id: "preset_stage_call_analysis",
      title: "Analyse a discovery call",
      description: "Requirements, objections and decision criteria from call notes.",
      promptText: FULL_CYCLE_STEPS[3].promptText,
      category: "management",
      favourite: false,
      order: 8,
    },
    {
      id: "preset_stage_proposal",
      title: "Draft a proposal",
      description: "Scope, approach, timeline and next steps from the discovery work.",
      promptText: FULL_CYCLE_STEPS[4].promptText,
      category: "management",
      favourite: false,
      order: 9,
    },
    {
      id: "preset_complaints",
      title: "Complaints inbox analysis",
      description: "Group this week's complaint emails into themes with counts.",
      promptText:
        "Analyse this week's complaints inbox and group the emails into themes with counts.",
      category: "custom",
      favourite: false,
      order: 10,
    },
  ];
}

export function getSeedPreset(presetId: string): ProtoPreset | undefined {
  return buildSeedPresets().find((preset) => preset.id === presetId);
}

// ---------------------------------------------------------------------------
// Agent activity report (rendered as formatted blocks by the chat UI)

export function buildActivityReportContent(): string {
  return [
    "## Agent activity report — last 7 days",
    "",
    "### Sales team",
    "- Calls made: 412",
    "- Calls connected: 168",
    "- Meetings booked: 23",
    "",
    "### Support team",
    "- Conversations handled: 286",
    "- Resolved without escalation: 241",
    "- Avg first response: 42s",
    "",
    "### Analyst team",
    "- Analyses run: 37",
    "- Reports delivered: 12",
    "- Data tables updated: 9",
    "",
    "### Task team",
    "- Tasks completed: 154",
    "- Approvals processed: 31",
    "- Automations fired: 78",
    "",
    "**Insight:** Connect rate is up 6 points week-on-week — the Tuesday 9–11am call blocks are booking the most meetings.",
  ].join("\n");
}

// ---------------------------------------------------------------------------
// Canned reply generator

export interface CannedReply {
  content: string;
  suggestions: string[];
}

const DEFAULT_SUGGESTIONS = [
  "Go deeper on this",
  "Turn this into a summary email",
  "Schedule this daily",
];

interface ReplyRule {
  match: RegExp;
  replies: string[];
  suggestions: string[];
}

const REPLY_RULES: ReplyRule[] = [
  {
    match: /complaint/,
    replies: [
      "I went through 47 complaint emails from the last 7 days and grouped them into 4 themes: delivery delays (19), billing confusion (12), onboarding friction (9) and feature requests filed as complaints (7). Delivery delays are up 30% on the previous week and cluster around two carriers. The other themes are flat or improving.",
      "This week's complaints inbox holds 38 emails across 3 main themes: response-time frustration (16), invoice disputes (13) and unclear cancellation steps (9). Response-time complaints doubled after Tuesday — they correlate with the support rota change that day.",
    ],
    suggestions: [
      "Draft replies to these emails",
      "Go deeper on the top issue",
      "Schedule this weekly",
    ],
  },
  {
    match: /draft (a )?repl/,
    replies: [
      "Drafted 19 personalised replies grouped by theme — each acknowledges the specific issue, gives a realistic resolution date and offers a follow-up call where the tone warranted it. They're staged for your review before anything is sent.",
      "I've prepared reply drafts for every email in the top theme. Each one references the customer's order details and proposes a concrete next step. Nothing sends until you approve.",
    ],
    suggestions: [
      "Queue drafts for approval",
      "Make the tone more formal",
      "Go deeper on the top issue",
    ],
  },
  {
    match: /go deeper|top issue/,
    replies: [
      "Digging into the top issue: 14 of the 19 cases trace back to a single handoff step where confirmations aren't being sent, so customers chase us instead. Fixing that one notification would remove roughly 70% of the volume. The remaining 5 cases are genuine exceptions that need a human decision.",
      "The deeper pattern: the issue spikes on Mondays and after releases, and 80% of affected customers are on the same plan tier. That points to a process gap rather than individual mistakes — I'd start with the workflow owner.",
    ],
    suggestions: [
      "Draft a fix proposal for the team",
      "Turn this into a summary email",
      "Schedule this weekly",
    ],
  },
  {
    match: /methodolog/,
    replies: [
      "Best fit is the phased-delivery methodology: the brief's fixed budget and 12-week window rule out open-ended discovery, and the integration requirements match the pattern we use for operations-heavy clients. Runner-up is the pilot-first approach — worth keeping if scope negotiation stalls.",
      "I compared the brief against our four delivery methodologies. Recommendation: outcome-based sprints — the client's requirements are clear but their internal stakeholders aren't aligned yet, so short cycles with visible checkpoints de-risk the engagement. Confidence: high.",
    ],
    suggestions: [
      "Build a call prep sheet",
      "Compare with the runner-up approach",
      "Summarise this for the exec team",
    ],
  },
  {
    match: /prep sheet|call prep/,
    replies: [
      "Call prep sheet ready. Top questions: current process ownership, volume peaks, and what 'success in 90 days' looks like to them. Stakeholders to confirm: operations lead and finance sign-off. Risks to probe: an incumbent supplier relationship and an unclear data-access policy.",
      "Prep sheet built from the brief: 8 discovery questions ordered by importance, 3 stakeholders to validate on the call, and 2 risks flagged — timeline compression and an unbudgeted integration. There's a suggested 30-minute agenda at the end.",
    ],
    suggestions: [
      "Analyse the discovery call notes",
      "Add pricing questions",
      "Turn this into a summary email",
    ],
  },
  {
    match: /discovery call/,
    replies: [
      "From the call notes: 6 confirmed requirements (3 new versus the brief), 2 objections — pricing structure and rollout speed — and their decision criteria are ease of adoption first, cost second. The operations lead is the real decision-maker; procurement joins only at contract stage.",
      "Call analysis done. The client confirmed the core scope, pushed back on the phased timeline, and revealed a hard deadline tied to their fiscal year. Their unstated concern is internal change management — worth addressing head-on in the proposal.",
    ],
    suggestions: [
      "Draft the proposal",
      "List unresolved objections",
      "Draft a follow-up email",
    ],
  },
  {
    match: /proposal/,
    replies: [
      "Proposal outline drafted: executive summary tied to their fiscal-year deadline, scope in three phases with the contested items moved to phase two, a timeline that shows value in week 4, and next steps ending in a decision date. The pricing section has two options — fixed and outcome-linked.",
      "Draft ready. It leads with the problem framing from the discovery call, maps each requirement to a deliverable, and pre-empts the rollout-speed objection with a pilot milestone. Open item: reference case selection — I shortlisted two.",
    ],
    suggestions: [
      "Tighten the executive summary",
      "Draft the pricing options",
      "Schedule this workflow weekly",
    ],
  },
  {
    match: /brief/,
    replies: [
      "Brief summarised. The client needs an operations overhaul across intake and reporting, with a budget signal in the mid-range band and a hard 12-week timeline. Three requirements are explicit, two are implied by their pain points. Open questions: data access and who owns sign-off.",
      "One-page summary ready: core ask is automating a manual triage process, success metric is response time, and the timeline is quarter-end. Budget isn't stated but the urgency language suggests pre-approved spend. Two clarifications needed before scoping.",
    ],
    suggestions: [
      "Match methodology to this brief",
      "List open questions for the client",
      "Draft a clarification email",
    ],
  },
  {
    match: /executive briefing|weekly executive/,
    replies: [
      "Executive briefing ready. Headlines: revenue pacing at 104% of plan, two enterprise deals slipped to next month, and support volume down 9% after the workflow change. Wins: the renewals push closed 11 of 12 accounts. Risks: hiring pipeline for the delivery team is thin. One decision needed: approve the Q3 capacity plan.",
      "This week's briefing: pipeline coverage at 3.1x, meetings booked up 18%, and delivery utilisation steady at 82%. The flag: churn risk on two mid-market accounts showing usage decline. Decision required: whether to fast-track the pricing experiment.",
    ],
    suggestions: [
      "Schedule this weekly",
      "Expand the risks section",
      "Turn this into a slide outline",
    ],
  },
  {
    match: /pipeline/,
    replies: [
      "Pipeline snapshot: 68 open opportunities, stage conversion strongest from qualified→proposal (61%), weakest at proposal→commit (28%). Nine deals are stalled 21+ days, mostly awaiting security review. Forecast confidence: medium-high — commit coverage is 1.4x target.",
      "Pipeline health: total value up 12% month-on-month, but ageing is creeping — median days-in-stage rose from 9 to 13. Three deals account for 40% of the quarter's forecast, which concentrates risk. Suggested focus: the five proposals sitting untouched for two weeks.",
    ],
    suggestions: [
      "Show stalled deals only",
      "Break down by team",
      "Schedule this weekly",
    ],
  },
  {
    match: /break down by agent|break down by team|by agent/,
    replies: [
      "Per-agent breakdown: the top performer made 96 calls with a 47% connect rate and 8 meetings; the median is 58 calls at 39%. The gap is call-block discipline, not scripts — top performers front-load calls before 11am. Two agents show connect rates 10+ points below the team; both joined this month.",
      "Breakdown ready. Meetings booked range from 2 to 8 per agent; connect rates cluster tightly (37–44%) so volume explains most of the difference. The morning shift outperforms the afternoon shift on every metric.",
    ],
    suggestions: [
      "Coach the newest two on call blocks",
      "Export as PDF",
      "Schedule this weekly",
    ],
  },
  {
    match: /export.*pdf|as pdf/,
    replies: [
      "The report has been packaged as a PDF with the summary table, per-team sections and the week-on-week comparison chart. In the live product this would land in your downloads and the shared reports folder.",
    ],
    suggestions: ["Email it to the leadership group", "Schedule this weekly"],
  },
  {
    match: /follow-up email|summary email|clarification email/,
    replies: [
      "Email drafted: three short paragraphs — context, the key findings in plain language, and one clear ask with a deadline. It reads in under a minute. Staged for your review before sending.",
      "Draft ready. Subject line options included, the body summarises the highlights in five bullet points, and there's a call-to-action at the end. Nothing sends until you approve.",
    ],
    suggestions: [
      "Make it shorter",
      "Queue it for approval",
      "Schedule this weekly",
    ],
  },
  {
    match: /schedule/,
    replies: [
      "Done — this will now run automatically and the results will appear here as a new chat each time, marked with a clock icon. You can change the cadence or pause it from the schedule settings.",
    ],
    suggestions: ["Run it once now", "Go deeper on this"],
  },
];

export function buildCannedReply(text: string, presetId?: string): CannedReply {
  const lower = text.toLowerCase();

  if (
    presetId === ACTIVITY_REPORT_PRESET_ID ||
    /agent activity report|activity report/.test(lower)
  ) {
    return {
      content: buildActivityReportContent(),
      suggestions: ["Schedule this weekly", "Break down by agent", "Export as PDF"],
    };
  }

  for (const rule of REPLY_RULES) {
    if (rule.match.test(lower)) {
      return {
        content: pick(rule.replies, text),
        suggestions: rule.suggestions,
      };
    }
  }

  if (/report/.test(lower)) {
    return {
      content: pick(
        [
          "Report compiled from this week's activity data: volumes are up 11% overall, quality metrics are steady, and one team is trending ahead of target. The full breakdown is grouped by team with week-on-week deltas.",
          "Here's the report: three sections covering output, outcomes and exceptions. The headline — outcomes improved while volume stayed flat, which suggests the recent process change is working.",
        ],
        text
      ),
      suggestions: ["Schedule this weekly", "Break down by agent", "Export as PDF"],
    };
  }

  return {
    content: pick(
      [
        "I looked into that using this week's data across your teams. The short version: the trend is positive, two items need attention, and I've noted one opportunity worth a closer look. Ask me to go deeper on any part.",
        "Done — I pulled the relevant activity and summarised it. The main signal is consistent with last week, with one notable exception I've flagged. Happy to break it down further or turn it into something shareable.",
        "Here's what I found: the numbers support your hunch, the underlying driver looks operational rather than seasonal, and there's a quick win available. Tell me which thread to pull on.",
      ],
      text
    ),
    suggestions: DEFAULT_SUGGESTIONS,
  };
}
